import { getStudioAccessToken } from '@/services/auth-provider'
import type { CogoXyzRegistrySnapshot } from './cogo-xyz-registry'

const DEFAULT_LIBRARY_HUB_PROXY_BASE_URL = '/api/library-hub'

export interface CogoXyzServerSyncOptions {
  baseUrl?: string
  /**
   * Trusted-server bearer token. Do not source this from VITE_* env because
   * browser bundles expose those values to users.
   */
  token?: string
  getAccessToken?: () => Promise<string | null>
  fetchImpl?: typeof fetch
}

export interface CogoXyzServerSyncResult {
  attempted: boolean
  ok: boolean
  status?: number
  error?: string
}

function envValue(key: string): string | undefined {
  const meta = import.meta as unknown as { env?: Record<string, string | undefined> }
  return meta.env?.[key]
}

function configuredBaseUrl(options: CogoXyzServerSyncOptions = {}): string | null {
  const raw = options.baseUrl ?? envValue('VITE_COGO_LIBRARY_HUB_URL') ?? DEFAULT_LIBRARY_HUB_PROXY_BASE_URL
  const trimmed = raw?.trim().replace(/\/$/, '')
  return trimmed || null
}

function configuredToken(options: CogoXyzServerSyncOptions = {}): string | null {
  const raw = options.token
  const trimmed = raw?.trim()
  return trimmed || null
}

function isSameOriginProxy(baseUrl: string): boolean {
  return baseUrl.startsWith('/')
}

export async function pushCogoXyzRegistrySnapshotToServer(
  snapshot: CogoXyzRegistrySnapshot,
  options: CogoXyzServerSyncOptions = {},
): Promise<CogoXyzServerSyncResult> {
  const baseUrl = configuredBaseUrl(options)
  if (!baseUrl) return { attempted: false, ok: false }
  const fetcher = options.fetchImpl ?? fetch
  const token = configuredToken(options)
  const useProxy = isSameOriginProxy(baseUrl)
  if (!token && !useProxy) {
    return {
      attempted: false,
      ok: false,
      error: 'Direct Library Hub sync requires a trusted token; use the same-origin proxy from browser code.',
    }
  }
  const userToken = token
    ? null
    : await (options.getAccessToken ?? getStudioAccessToken)()
  if (!token && !userToken) return { attempted: false, ok: false, error: 'Sign in required for Library Hub sync.' }
  try {
    const response = await fetcher(`${baseUrl}/v1/library/snapshots`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token ?? userToken}`,
      },
      body: JSON.stringify({ snapshot }),
    })
    return {
      attempted: true,
      ok: response.ok,
      status: response.status,
      error: response.ok ? undefined : await response.text(),
    }
  } catch (error) {
    return {
      attempted: true,
      ok: false,
      error: error instanceof Error ? error.message : String(error),
    }
  }
}
